/**
 * archive-quiz.ts
 *
 * Student-facing "歷屆考題" browser.
 *
 * Screens:
 *   1. Filter screen  — pick subject + source (真題 / 擬真 / 全部)
 *   2. Question list  — shows prompt summary + KP + source tag
 *   3. Quiz overlay   — reuses existing QuizOverlay for the actual question
 */

import type { Subject } from '../shared/types';
import { loadArchiveFiltered } from './content/archive-loader';
import type { ArchiveQuestionWithMeta, ArchiveSource } from './content/archive-loader';
import { QuizOverlay } from './quiz/overlay';
import { showXpPopup, flashCard } from './quiz/feedback';
import type { GeneratedQuestion } from './generators/math/types';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ArchiveQuizActions {
  onBack: () => void;
}

export function renderArchiveQuiz(container: HTMLElement, actions: ArchiveQuizActions): void {
  const root = document.createElement('div');
  root.className = 'archive-quiz';
  container.appendChild(root);
  showFilterScreen(root, actions);
}

// ---------------------------------------------------------------------------
// Screen 1: Filter
// ---------------------------------------------------------------------------

const SUBJECT_OPTS: { id: Subject; label: string; emoji: string }[] = [
  { id: 'math',    label: '數學', emoji: '⚡' },
  { id: 'chinese', label: '國語', emoji: '🔮' },
  { id: 'english', label: '英語', emoji: '🤖' },
  { id: 'nature',  label: '自然', emoji: '🌀' },
  { id: 'social',  label: '社會', emoji: '🏰' },
];

type SourceFilter = 'all' | 'crawled' | 'simulated';

const SOURCE_OPTS: { id: SourceFilter; label: string }[] = [
  { id: 'all',       label: '全部' },
  { id: 'crawled',   label: '真題' },
  { id: 'simulated', label: '擬真' },
];

function showFilterScreen(root: HTMLElement, actions: ArchiveQuizActions): void {
  root.innerHTML = `
    <h1>📜 歷屆考題</h1>
    <p class="archive-desc">按科目與來源篩選,挑選題目練習。</p>

    <div class="filter-section">
      <h3>科目</h3>
      <div class="filter-row" id="subject-row">
        <button class="filter-btn active" data-subject="">全部科目</button>
        ${SUBJECT_OPTS.map(s =>
          `<button class="filter-btn" data-subject="${s.id}">${s.emoji} ${s.label}</button>`
        ).join('')}
      </div>
    </div>

    <div class="filter-section">
      <h3>來源</h3>
      <div class="filter-row" id="source-row">
        ${SOURCE_OPTS.map((s, i) =>
          `<button class="filter-btn${i === 0 ? ' active' : ''}" data-source="${s.id}">${s.label}</button>`
        ).join('')}
      </div>
    </div>

    <div class="filter-actions">
      <button class="primary-btn" id="btn-search">查詢題目</button>
      <button class="secondary-btn" id="btn-back">回主畫面</button>
    </div>
  `;

  let selectedSubject: Subject | undefined;
  let selectedSource: SourceFilter = 'all';

  // Subject toggle
  const subjectRow = root.querySelector('#subject-row')!;
  subjectRow.querySelectorAll<HTMLButtonElement>('.filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      subjectRow.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const sv = btn.dataset.subject;
      selectedSubject = (sv && sv.length > 0) ? sv as Subject : undefined;
    });
  });

  // Source toggle
  const sourceRow = root.querySelector('#source-row')!;
  sourceRow.querySelectorAll<HTMLButtonElement>('.filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      sourceRow.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      selectedSource = btn.dataset.source as SourceFilter;
    });
  });

  root.querySelector('#btn-search')!.addEventListener('click', () => {
    const questions = loadArchiveFiltered({
      subject: selectedSubject,
      source: selectedSource,
    });
    showListScreen(root, questions, actions);
  });

  root.querySelector('#btn-back')!.addEventListener('click', () => {
    root.remove();
    actions.onBack();
  });
}

// ---------------------------------------------------------------------------
// Screen 2: Question List
// ---------------------------------------------------------------------------

function sourceTag(source: ArchiveSource): string {
  if (source === 'crawled')   return '<span class="source-tag tag-crawled">公開試題</span>';
  if (source === 'simulated') return '<span class="source-tag tag-simulated">擬真題</span>';
  return '<span class="source-tag tag-manual">手動</span>';
}

function showListScreen(
  root: HTMLElement,
  questions: ArchiveQuestionWithMeta[],
  actions: ArchiveQuizActions,
): void {
  if (questions.length === 0) {
    root.innerHTML = `
      <h1>📜 歷屆考題</h1>
      <p class="archive-empty">目前沒有符合條件的題目。<br>請先執行爬蟲腳本或手動新增題目。</p>
      <button class="secondary-btn" id="btn-back-filter">回篩選</button>
      <button class="secondary-btn" id="btn-back-home">回主畫面</button>
    `;
    root.querySelector('#btn-back-filter')!.addEventListener('click', () => showFilterScreen(root, actions));
    root.querySelector('#btn-back-home')!.addEventListener('click', () => { root.remove(); actions.onBack(); });
    return;
  }

  root.innerHTML = `
    <h1>📜 歷屆考題</h1>
    <p class="archive-count">共 ${questions.length} 題</p>
    <div class="archive-list" id="q-list"></div>
    <div class="filter-actions">
      <button class="secondary-btn" id="btn-back-filter">回篩選</button>
      <button class="secondary-btn" id="btn-back-home">回主畫面</button>
    </div>
  `;

  const list = root.querySelector('#q-list')!;
  questions.forEach((q, idx) => {
    const item = document.createElement('div');
    item.className = 'archive-item';
    const promptSnippet = q.prompt.length > 60 ? q.prompt.slice(0, 60) + '…' : q.prompt;
    const provenance = q.sourceLabel ? `來源:${q.sourceLabel}` : '';
    item.innerHTML = `
      <div class="archive-item-head">
        ${sourceTag(q.source)}
        <span class="archive-kp">${escapeHtml(q.kp)}</span>
      </div>
      <div class="archive-prompt">${escapeHtml(promptSnippet)}</div>
      ${provenance ? `<div class="archive-provenance">${escapeHtml(provenance)}</div>` : ''}
    `;
    item.addEventListener('click', () => showQuizScreen(root, questions, idx, actions));
    list.appendChild(item);
  });

  root.querySelector('#btn-back-filter')!.addEventListener('click', () => showFilterScreen(root, actions));
  root.querySelector('#btn-back-home')!.addEventListener('click', () => { root.remove(); actions.onBack(); });
}

// ---------------------------------------------------------------------------
// Screen 3: Quiz (single question, reuses QuizOverlay)
// ---------------------------------------------------------------------------

function showQuizScreen(
  root: HTMLElement,
  questions: ArchiveQuestionWithMeta[],
  startIdx: number,
  actions: ArchiveQuizActions,
): void {
  root.innerHTML = '';
  const wrapper = document.createElement('div');
  wrapper.className = 'archive-quiz-wrapper';
  root.appendChild(wrapper);

  let currentIdx = startIdx;
  let overlay: QuizOverlay | null = null;
  let answered = false;

  const renderQuestion = (): void => {
    wrapper.innerHTML = '';
    answered = false;

    const q = questions[currentIdx];
    const navInfo = document.createElement('div');
    navInfo.className = 'archive-nav-info';
    navInfo.innerHTML = `
      <span>${currentIdx + 1} / ${questions.length}</span>
      ${sourceTag(q.source)}
      ${q.sourceLabel ? `<span class="archive-provenance">${escapeHtml(q.sourceLabel)}</span>` : ''}
    `;
    wrapper.appendChild(navInfo);

    // QuizOverlay mounts itself into wrapper
    overlay = new QuizOverlay({
      container: wrapper,
      onAnswer: (answer) => {
        if (answered) return;
        answered = true;

        const correct = checkAnswer(q, answer);
        const card = wrapper.querySelector('.card') as HTMLElement | null;
        if (card) flashCard(card, correct ? 'correct' : 'wrong');
        if (correct) showXpPopup('+10 ⚡', '#4FC3F7');

        // Show explanation
        const explanation = document.createElement('div');
        explanation.className = 'archive-explanation';
        explanation.textContent = q.explanation;
        wrapper.appendChild(explanation);

        // Nav buttons
        const nav = document.createElement('div');
        nav.className = 'archive-nav-btns';
        if (currentIdx < questions.length - 1) {
          const next = document.createElement('button');
          next.className = 'primary-btn';
          next.textContent = '下一題 →';
          next.addEventListener('click', () => {
            overlay?.destroy();
            currentIdx++;
            renderQuestion();
          });
          nav.appendChild(next);
        }
        const backList = document.createElement('button');
        backList.className = 'secondary-btn';
        backList.textContent = '回題目列表';
        backList.addEventListener('click', () => {
          overlay?.destroy();
          const fresh = loadArchiveFiltered({});
          showListScreen(root, fresh, actions);
        });
        nav.appendChild(backList);
        wrapper.appendChild(nav);
      },
    });

    // QuizOverlay.render expects a GeneratedQuestion — convert via helper
    overlay.render(toGeneratedQuestion(q), 'challenge', { current: currentIdx + 1, total: questions.length });
  };

  renderQuestion();
}

// ---------------------------------------------------------------------------
// Type conversion
// ---------------------------------------------------------------------------

function toGeneratedQuestion(q: ArchiveQuestionWithMeta): GeneratedQuestion {
  if (q.kind === 'mcq') {
    return {
      kind: 'mcq',
      prompt: q.prompt,
      explanation: q.explanation,
      kp: q.kp,
      options: q.options ?? [],
      correctAnswer: typeof q.correctAnswer === 'number' ? q.correctAnswer : 0,
    };
  } else {
    return {
      kind: 'fill',
      prompt: q.prompt,
      explanation: q.explanation,
      kp: q.kp,
      correctAnswer: String(q.correctAnswer),
      acceptedAnswers: q.acceptedAnswers,
    };
  }
}

// ---------------------------------------------------------------------------
// Answer checking
// ---------------------------------------------------------------------------

function checkAnswer(q: ArchiveQuestionWithMeta, answer: number | string): boolean {
  if (q.kind === 'mcq') {
    return answer === q.correctAnswer;
  }
  // fill
  const userStr = String(answer).trim().toLowerCase();
  const correct = String(q.correctAnswer).trim().toLowerCase();
  if (userStr === correct) return true;
  if (q.acceptedAnswers) {
    return q.acceptedAnswers.some((a: string) => a.trim().toLowerCase() === userStr);
  }
  return false;
}

// ---------------------------------------------------------------------------
// Utils
// ---------------------------------------------------------------------------

function escapeHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}
